/**
 * useLowBalanceWarning Hook
 *
 * Monitors the user's OLV balance in real-time and determines
 * if it falls below the warning ($10) or critical (at/below $0) thresholds.
 *
 * Two-tier system:
 * - Warning tier ($10): Shows warning banner, user can still run tasks
 * - Critical tier (<= $0, 2026-08-14 server-hard-rule alignment): Blocks
 *   input in production mode only — the wallet is actually EMPTY
 *
 * Testing mode behavior:
 * - Warnings always show (both testing and production)
 * - Blocking only applies in production (TESTING_MODE=false AND TESTING_ENVIRONMENT=false)
 *
 * Entitlement bypass (2026-05-13 hotfix):
 * - Trial users with credit + future expiry, `is_client_free` users,
 *   enterprise accounts (`accounts/{accountId}/billing/main.plan ===
 *   'enterprise' && subscriptionStatus === 'active'`), and the legacy
 *   `users/{uid}.trial.active === true` flag all bypass the gate
 *   entirely. See `entitlementBypass.ts` for the canonical rules.
 *   This guards a Fernando-Hidalgo-class regression where an
 *   active-trial enterprise tenant had no OLV wallet doc and was
 *   refused chat access at $0 OLV balance even though usage was paid
 *   at the account level.
 */

import { useMemo } from 'react';
import { useMaybeWalletSummary } from '../../context/WalletSummaryContext';
import { useMaybeOLVRate } from '../../context/OLVRateContext';
import { WARNING_BALANCE_THRESHOLD_USD, CRITICAL_BALANCE_THRESHOLD_USD } from './constants';
import { shouldTagAsTest } from '../../config/agenticWorkflowConfig';
import { useEntitlementBypass } from './useEntitlementBypass';
import type { EntitlementBypassReason } from './entitlementBypass';
import { usableAllocationPr0 } from '../../config/planAllocation';
import { wuToUsd } from '../../config/workUnits';

// ============================================================================
// Types
// ============================================================================

export interface LowBalanceState {
  /** Whether balance is below warning threshold ($10) */
  isWarningBalance: boolean;
  /** Whether balance is at/below the critical threshold ($0 — wallet empty) */
  isCriticalBalance: boolean;
  /** @deprecated Use isWarningBalance or isCriticalBalance instead */
  isLowBalance: boolean;
  /** Current balance in USD (real-time calculation) */
  balanceUsd: number;
  /** Current balance in OLV */
  balanceOlv: number;
  /** Whether to show warning banner (< $10, always shows in both modes) */
  shouldWarn: boolean;
  /** Whether to block chat input (< $3, production mode only) */
  shouldBlock: boolean;
  /** Whether wallet/rate data is still loading */
  isLoading: boolean;
  /** Whether testing mode is active (bypasses blocking) */
  isTestingMode: boolean;
  /**
   * Whether any entitlement bypass condition holds (trial, client-free,
   * enterprise, legacy trial.active). When true, blocking and warning
   * are forced to `false` regardless of OLV balance.
   */
  hasEntitlementBypass: boolean;
  /** Which entitlement bypass condition(s) matched, if any. */
  entitlementBypassReasons: EntitlementBypassReason[];
  /**
   * Is the acting account's spend balance stop armed? False ONLY when the
   * account is deliberately allowed to run past zero
   * (accounts/{id}/config/spendLimits.balanceStop.enabled === false), which is
   * the same fact the SERVER already uses to suppress the low-balance alert
   * (functions/src/notifications/lowBalanceDecision.ts). When it is false there
   * is no low-balance condition to report, so warning and blocking are both
   * forced off. Defaults to armed whenever unknown.
   */
  balanceStopArmed: boolean;
  /**
   * Whether a Firebase-Auth-confirmed user backs these numbers (2026-08-14).
   * Lets consumers distinguish a real $0 balance from the signed-out default
   * — a guest must never be treated as "out of PRO Work Units".
   */
  hasUser: boolean;
  /**
   * A paid plan's usable monthly allocation, in pr0 (2026-10-11): spent
   * before the wallet, so the warning and the block count it — a paying
   * account with plan pr0 left is never "out of balance" at a $0 wallet.
   * 0 when the account has no paid plan.
   */
  allocationPr0: number;
  /**
   * Whether the wallet summary has actually loaded for that user (2026-08-14).
   * The entitlement-bypass branch reports isLoading:false so chat stays live,
   * which left balanceUsd momentarily defaulted — consumers deciding EMPTY
   * (<= $0) must additionally require balanceSettled.
   */
  balanceSettled: boolean;
}

// ============================================================================
// Fallback rate when OLV rate context unavailable
// ============================================================================

const FALLBACK_RATE_USD = 0.001;

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to monitor balance and determine warning/blocking states.
 *
 * Threshold behavior:
 * - Balance > $10: Normal operation
 * - $0 < Balance < $10: Warning shown, can still run tasks
 * - Balance <= $0: Blocked in production, warning only in testing (the
 *   server balance stop pauses work at empty — 2026-08-14 alignment)
 *
 * Edge case handling:
 * - User not logged in: Don't block (allow anonymous browsing)
 * - Contexts unavailable: Fail open (don't block)
 * - Data loading: Don't block until data is confirmed
 * - Network errors: Fail open
 * - Entitlement bypass: Skip gate entirely (see file header)
 */
export function useLowBalanceWarning(): LowBalanceState {
  const walletSummary = useMaybeWalletSummary();
  const olvRate = useMaybeOLVRate();
  const entitlement = useEntitlementBypass();

  return useMemo(() => {
    // Determine if in testing mode (either flag bypasses blocking)
    const isTestingMode = shouldTagAsTest();
    const hasEntitlementBypass = entitlement.hasBypass;
    const entitlementBypassReasons = entitlement.reasons;
    const balanceStopArmed = entitlement.balanceStopArmed;

    // Default state for unavailable/loading contexts
    const defaultState: LowBalanceState = {
      isWarningBalance: false,
      isCriticalBalance: false,
      isLowBalance: false,
      balanceUsd: 0,
      balanceOlv: 0,
      shouldWarn: false,
      shouldBlock: false,
      isLoading: true,
      isTestingMode,
      hasEntitlementBypass,
      entitlementBypassReasons,
      balanceStopArmed,
      hasUser: false,
      balanceSettled: false,
      allocationPr0: 0,
    };

    // ── AUTH GATE (2026-07-08): a low-balance warning/block is meaningless for a
    // ── signed-out visitor. Gate on the Firebase-Auth-confirmed user exposed by
    // ── WalletSummaryContext (`hasUser`) — NOT the stale localStorage AuthContext
    // ── — so a logged-out user (INCLUDING one who was signed in earlier this
    // ── session, whose default $0 summary would otherwise read as "critically
    // ── low") never sees the banner or gets input-blocked. Settled, non-warning.
    if (!walletSummary || !walletSummary.hasUser) {
      return {
        ...defaultState,
        isLoading: false,
        shouldWarn: false,
        shouldBlock: false,
        hasEntitlementBypass: false,
        entitlementBypassReasons: [],
        balanceStopArmed,
        hasUser: false,
        balanceSettled: false,
        allocationPr0: 0,
      };
    }

    // ── Short-circuit: any entitlement bypass condition unblocks the
    // ── gate regardless of wallet state. We still expose the
    // ── as-computed balance values below for UI display, but only
    // ── after the wallet summary has loaded; until then we return
    // ── a non-loading, non-blocking state so the chat UI is live.
    if (hasEntitlementBypass) {
      const olvBalance = walletSummary?.summary.olvBalance ?? 0;
      const balanceUsd = olvRate
        ? olvRate.olvToUsd(olvBalance)
        : olvBalance * FALLBACK_RATE_USD;
      return {
        isWarningBalance: false,
        isCriticalBalance: false,
        isLowBalance: false,
        balanceUsd,
        balanceOlv: olvBalance,
        shouldWarn: false,
        shouldBlock: false,
        isLoading: false,
        isTestingMode,
        hasEntitlementBypass: true,
        entitlementBypassReasons,
        balanceStopArmed,
        hasUser: true,
        balanceSettled: !walletSummary.isLoading,
        allocationPr0: usableAllocationPr0(walletSummary.planAllocation, Date.now()),
      };
    }

    // ── Short-circuit: the account is deliberately allowed to run past zero
    // ── (spendLimits.balanceStop.enabled === false), so there is no
    // ── low-balance condition to report. The SERVER already reaches this
    // ── conclusion for the same account on the same fact
    // ── (notifications/lowBalanceDecision.ts, Miles 2026-09-09); this is the
    // ── client half, which had been warning and BLOCKING accounts the server
    // ── had already decided not to nag. Balance values are still exposed for
    // ── display — the wallet page keeps showing the real number.
    if (!balanceStopArmed) {
      const olvBalance = walletSummary?.summary.olvBalance ?? 0;
      const balanceUsd = olvRate
        ? olvRate.olvToUsd(olvBalance)
        : olvBalance * FALLBACK_RATE_USD;
      return {
        isWarningBalance: false,
        isCriticalBalance: false,
        isLowBalance: false,
        balanceUsd,
        balanceOlv: olvBalance,
        shouldWarn: false,
        shouldBlock: false,
        isLoading: false,
        isTestingMode,
        hasEntitlementBypass,
        entitlementBypassReasons,
        balanceStopArmed: false,
        hasUser: true,
        balanceSettled: !walletSummary.isLoading,
        allocationPr0: usableAllocationPr0(walletSummary.planAllocation, Date.now()),
      };
    }

    // If wallet context is not available, fail open (don't block)
    if (!walletSummary) {
      return defaultState;
    }

    // If still loading, don't block yet
    if (walletSummary.isLoading) {
      return defaultState;
    }

    // If entitlement query is still loading we cannot yet decide whether
    // the user is exempt — be conservative and don't block.
    if (entitlement.isLoading) {
      return defaultState;
    }

    // Get OLV balance from wallet summary
    const olvBalance = walletSummary.summary.olvBalance ?? 0;

    // Calculate USD value using OLV rate context or fallback
    // This is the real-time calculation based on current OLV rate
    let balanceUsd: number;
    if (olvRate) {
      balanceUsd = olvRate.olvToUsd(olvBalance);
    } else {
      // Fallback calculation if rate context unavailable
      balanceUsd = olvBalance * FALLBACK_RATE_USD;
    }

    // A paid plan spends its monthly allocation before the wallet (2026-10-11),
    // and the server's balance stop counts both — so do these thresholds.
    const allocationPr0 = usableAllocationPr0(walletSummary.planAllocation, Date.now());
    const spendableUsd = balanceUsd + wuToUsd(allocationPr0);

    // Determine threshold states (real-time OLV calculation).
    // Critical is `<=` (2026-08-14): the server balance stop pauses work at
    // EXACTLY empty, so a $0.00 wallet must read as critical, not merely low.
    const isWarningBalance = spendableUsd < WARNING_BALANCE_THRESHOLD_USD;  // < $10
    const isCriticalBalance = spendableUsd <= CRITICAL_BALANCE_THRESHOLD_USD; // <= $0

    // Warning shows for BOTH testing and production when below warning threshold
    // This ensures users always see warnings regardless of mode
    const shouldWarn = isWarningBalance;

    // Blocking ONLY applies in production mode AND when below critical threshold
    // Testing mode bypasses blocking to allow development/testing
    const shouldBlock = isCriticalBalance && !isTestingMode;

    return {
      isWarningBalance,
      isCriticalBalance,
      isLowBalance: isWarningBalance, // Backward compatibility
      balanceUsd,
      balanceOlv: olvBalance,
      shouldWarn,
      shouldBlock,
      isLoading: false,
      isTestingMode,
      hasEntitlementBypass: false,
      entitlementBypassReasons,
      balanceStopArmed: true,
      hasUser: true,
      balanceSettled: true,
      allocationPr0,
    };
  }, [walletSummary, olvRate, entitlement]);
}
